import { Fragment, useId, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { ChevronDown, Filter as FilterIcon, Route, Users } from "lucide-react";
import { liftPhrase, liftSentence, type LiftFeature, type LiftReport } from "@metrickle/schema";
import { useGoals, useLift, useTasks } from "../lib/api";
import { useFilteredLink, useStatsParams } from "../lib/params";
import { fmtMoney, fmtNumber, fmtPercent, fmtPoints } from "../lib/format";
import { AppHeader, useAppId } from "../components/app-header";
import { StatsToolbar } from "../components/layout";
import { AskWhy } from "../components/ask-why";
import { StatTile } from "../components/stats";
import { ChartLegend } from "../components/charts/LineChart";
import { Badge, Button, ButtonLink, Callout, Card, CardHeader, Disclosure, Drawing, Empty, ErrorState, InlineCode, SelectField, Skeleton, cx } from "../components/ui";

/** What sets converters apart: traits ranked by how much more or less likely visitors with them converted. */
export function Converters() {
  const appId = useAppId();
  const [params, setParams] = useSearchParams();
  const goal = params.get("goal") ?? undefined;
  const task = params.get("task") ?? undefined;
  const goals = useGoals(appId);
  const tasks = useTasks(appId);
  const q = useLift(appId, task ? { task } : { goal });
  const r = q.data;

  const value = task ? `task:${task}` : goal ? `goal:${goal}` : "any";
  const items = [
    { value: "any", label: "Any goal" },
    ...(goals.data ?? []).map((g) => ({ value: `goal:${g.id}`, label: g.name })),
    ...(tasks.data ?? []).map((t) => ({ value: `task:${t.id}`, label: `Task: ${t.name}` })),
  ];
  const choose = (v: string) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("goal");
      next.delete("task");
      const [kind, id] = [v.slice(0, v.indexOf(":")), v.slice(v.indexOf(":") + 1)];
      if (kind === "goal" || kind === "task") next.set(kind, id);
      return next;
    });

  const nothingToConvert = goals.data?.length === 0 && tasks.data?.length === 0;

  return (
    <>
      <AppHeader title="What sets converters apart" tabTitle="Converters" />
      <StatsToolbar appId={appId}>
        {/* The label text stays for screen readers on narrow screens, where it's visually hidden. */}
        <label className="flex max-w-full items-center gap-2">
          <span className="sr-only text-sm text-ink-2 sm:not-sr-only">Converting means</span>
          <SelectField className="w-56 max-w-full" items={items} value={value} onChange={choose} />
        </label>
      </StatsToolbar>

      {(goals.isError || tasks.isError) && (
        <Card className="mb-4">
          <ErrorState title="Couldn't load your goals and tasks" error={goals.error ?? tasks.error} />
        </Card>
      )}
      {nothingToConvert ? (
        <Card>
          <Empty title="Nothing to count as converting yet" illustration={<Drawing name="createFirst" />}>
            Add a goal, like a sign-up or a purchase, and this page shows what the visitors who reached it did differently.{" "}
            <Link className="text-accent-text underline" to={`/apps/${appId}/goals`}>
              Add a goal
            </Link>
          </Empty>
        </Card>
      ) : q.isError ? (
        <Card>
          <ErrorState error={q.error} />
        </Card>
      ) : q.isPending && !r ? (
        <Skeleton className="h-96" />
      ) : !r || r.visitors === 0 ? (
        <Card>
          <Empty title="No visitors in this period" illustration={<Drawing name="emptyPeriod" />}>
            Widen the date range or remove filters.
          </Empty>
        </Card>
      ) : (
        <Report r={r} appId={appId} />
      )}
    </>
  );
}

function Report({ r, appId }: { r: LiftReport; appId: string }) {
  const clear = r.features.filter((f) => f.clear);
  const less = clear.filter((f) => f.z < 0);
  // A step nearly every converter passes through is part of the route, not a difference: kept apart so it doesn't crowd out the rest.
  const steps = clear.filter((f) => f.z > 0 && f.coverage >= 0.95);
  const more = clear.filter((f) => f.z > 0 && f.coverage < 0.95);
  const unclear = r.features.filter((f) => !f.clear);
  // Traits overlap (the same visitors saw a page and landed on it), so lead with the biggest, never a sum.
  const leak = less.reduce<LiftFeature | null>((best, f) => (!best || f.atStake > best.atStake ? f : best), null);
  const signal = more.reduce<LiftFeature | null>((best, f) => (!best || (f.lift ?? Infinity) > (best.lift ?? Infinity) ? f : best), null);

  return (
    <div className="space-y-4">
      <Card className="@container">
        <div className="grid grid-cols-1 divide-y divide-line @lg:grid-cols-3 @lg:divide-x @lg:divide-y-0">
          <StatTile label="Convert" value={fmtPercent(r.rate)} note={`${fmtNumber(r.converted)} of ${fmtNumber(r.visitors)} visitors`} />
          {leak && Math.round(leak.atStake) > 0 ? (
            <StatTile
              label="Biggest leak"
              value={<span className="text-bad">{fmtNumber(Math.round(leak.atStake))} lost</span>}
              note={
                <>
                  <Words text={label(leak)} />: {fmtPercent(leak.rate)} vs {fmtPercent(leak.rateWithout)}
                  {leak.moneyAtStake ? `, ${fmtMoney(leak.moneyAtStake)}` : ""}
                </>
              }
            />
          ) : (
            <StatTile label="Biggest leak" value={<span className="text-ink-3">None clear</span>} note="Nothing clearly goes with converting less" />
          )}
          {signal ? (
            <StatTile label="Strongest signal" value={<span className="text-good"><Words text={moreWord(signal)} /></span>} note={<Words text={`${label(signal)}: ${fmtPercent(signal.rate)} vs ${fmtPercent(signal.rateWithout)}`} />} />
          ) : (
            <StatTile label="Strongest signal" value={<span className="text-ink-3">None clear</span>} note="Nothing clearly goes with converting more" />
          )}
        </div>
      </Card>

      <Doubt />

      {clear.length === 0 ? (
        <Card>
          <Empty title="Nothing clear yet" icon={<Users size={24} aria-hidden />}>
            Each trait needs at least {fmtNumber(r.minVisitors)} visitors with it and {fmtNumber(r.minVisitors)} without it, and a big enough difference to rule out chance. Widen the date range to
            include more visitors.
          </Empty>
        </Card>
      ) : (
        <>
          {less.length > 0 && (
            <Card>
              <CardHeader title="Holding conversions back" subtitle="Visitors with these converted less often than everyone else. Most conversions lost first." />
              <Traits rows={less} report={r} appId={appId} tone="bad" />
            </Card>
          )}
          {more.length > 0 && (
            <Card>
              <CardHeader title="Go with converting" subtitle="Visitors with these converted more often than everyone else. Strongest first." />
              <Traits rows={more} report={r} appId={appId} tone="good" />
            </Card>
          )}
        </>
      )}

      {steps.length > 0 && (
        <Fold title="Steps on the way" count={steps.length} note="Almost every converter passed through these, so they're likely part of the route rather than a difference.">
          <Traits rows={steps} report={r} appId={appId} tone="unclear" />
        </Fold>
      )}
      {unclear.length > 0 && (
        <Fold title="Too early to tell" count={unclear.length} note="These differences could still be chance: too few visitors, or too small a gap.">
          <Traits rows={unclear} report={r} appId={appId} tone="unclear" />
        </Fold>
      )}
    </div>
  );
}

/** A collapsed card for rows that don't lead. */
function Fold({ title, count, note, children }: { title: string; count: number; note: string; children: ReactNode }) {
  return (
    <Card>
      <Disclosure summary={`${title} (${fmtNumber(count)})`} className="[&>summary]:mx-4 [&>summary]:my-3">
        <p className="px-4 pb-2 text-xs text-ink-3">{note}</p>
        {children}
      </Disclosure>
    </Card>
  );
}

/** The caveat, one line until asked for. */
function Doubt() {
  return (
    <Disclosure summary="Patterns, not proof">
      <Callout className="max-w-prose">
        People who look at plans may already mean to buy. Change one thing and watch this number before counting on it, and ask visitors why.
      </Callout>
    </Disclosure>
  );
}

type Tone = "bad" | "good" | "unclear";

const TONE: Record<Tone, { text: string; bg: string; color: string }> = {
  bad: { text: "text-bad", bg: "bg-bad", color: "var(--bad)" },
  good: { text: "text-good", bg: "bg-good", color: "var(--good)" },
  unclear: { text: "text-ink-2", bg: "bg-ink-3", color: "var(--ink-3)" },
};

/** Traits as rows: what they did, their rate against everyone else's on a shared scale, and the one number that matters. */
function Traits({ rows, report, appId, tone }: { rows: LiftFeature[]; report: LiftReport; appId: string; tone: Tone }) {
  const max = scaleMax(rows);
  return (
    <div className="border-t border-line">
      <div aria-hidden className="grid items-center gap-4 px-4 pt-2 text-[11px] text-ink-3 sm:grid-cols-[minmax(0,18rem)_minmax(0,1fr)_7rem]">
        <span className="hidden sm:block" />
        <span className="flex items-center justify-between">
          <span>0%</span>
          <ChartLegend
            inline
            series={[
              { id: "these", label: "these visitors", color: TONE[tone].color, shape: "dot" },
              { id: "rest", label: "everyone else", color: "var(--ink-3)", shape: "rule" },
            ]}
          />
          <span>{fmtPercent(max, 0)}</span>
        </span>
        <span className="hidden sm:block" />
      </div>
      <ul className="divide-y divide-line">
        {rows.map((f) => (
          <Trait key={`${f.kind}|${f.field}|${f.key}`} f={f} report={report} appId={appId} max={max} tone={tone} />
        ))}
      </ul>
    </div>
  );
}

function Trait({ f, report, appId, max, tone }: { f: LiftFeature; report: LiftReport; appId: string; max: number; tone: Tone }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const step = f.coverage >= 0.95;
  const lost = Math.round(f.atStake);
  const headline = tone === "bad" ? `${fmtNumber(lost)} lost` : tone === "good" ? moreWord(f) : null;

  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className={cx(
          "grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-left hover:bg-surface-2 focus-visible:outline-offset-[-2px] sm:grid-cols-[minmax(0,18rem)_minmax(0,1fr)_7rem]",
          open && "bg-surface-2",
        )}
      >
        <span className="flex min-w-0 items-center gap-2 text-sm text-ink">
          <ChevronDown aria-hidden size={14} className={cx("shrink-0 text-ink-3 transition-transform motion-reduce:transition-none", !open && "-rotate-90")} />
          <span className="shrink-0 rounded-md bg-surface-3 px-1.5 py-0.5 text-[11px] text-ink-2">{kindLabel(f)}</span>
          <span className="min-w-0 truncate">
            <Words text={label(f)} />
          </span>
        </span>
        <span className="order-last col-span-2 sm:order-none sm:col-span-1">
          <Bar f={f} max={max} tone={tone} />
        </span>
        <span className="text-right tabular">
          {headline && <span className={cx("block text-sm font-semibold", TONE[tone].text)}><Words text={headline} /></span>}
          <span className="block text-xs text-ink-3">
            {fmtPercent(f.rate)} vs {fmtPercent(f.rateWithout)}
          </span>
        </span>
        <span className="sr-only">
          . {liftSentence(f).replaceAll("`", "")}
          {tone === "bad" && lost > 0 ? ` About ${fmtNumber(lost)} conversions lost this period.` : ""}
          {step ? " Almost every converter passed here: likely a step on the way, not a difference." : ""}
        </span>
      </button>
      {open && (
        <div id={id} className="space-y-3 bg-surface-2 px-4 pt-1 pb-4 sm:pl-10">
          <p className="max-w-prose text-sm text-ink-2">
            <Words text={liftSentence(f)} /> {fmtNumber(f.visitors)} visitors had this.
            {tone === "bad" && lost > 0 && (
              <>
                {" "}
                About {fmtNumber(lost)} conversions{f.moneyAtStake ? ` (${fmtMoney(f.moneyAtStake)})` : ""} short of everyone else's rate.
              </>
            )}
          </p>
          {step && (
            <p className="text-xs text-ink-3">
              <Badge>Step on the way</Badge> Almost every converter passed here, so it's likely part of the route rather than a difference.
            </p>
          )}
          <Actions f={f} report={report} appId={appId} />
        </div>
      )}
    </li>
  );
}

/** Two rates on one line: a dot for these visitors, a dashed tick for everyone else, and the gap between filled in. */
function Bar({ f, max, tone }: { f: LiftFeature; max: number; tone: Tone }) {
  const at = (v: number) => `${Math.min(100, (v / max) * 100)}%`;
  const lo = Math.min(f.rate, f.rateWithout);
  const hi = Math.max(f.rate, f.rateWithout);
  return (
    <span aria-hidden className="relative block h-5">
      <span className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 rounded bg-line" />
      <span className={cx("absolute top-1/2 h-1 -translate-y-1/2 rounded opacity-35", TONE[tone].bg)} style={{ left: at(lo), width: `calc(${at(hi)} - ${at(lo)})` }} />
      <span className="absolute top-0.5 h-4 -translate-x-1/2 border-l-[1.5px] border-dashed border-ink-3" style={{ left: at(f.rateWithout) }} />
      <span className={cx("absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-surface", TONE[tone].bg)} style={{ left: at(f.rate) }} />
    </span>
  );
}

function Actions({ f, report, appId }: { f: LiftFeature; report: LiftReport; appId: string }) {
  const params = useStatsParams();
  const link = useFilteredLink();
  const filter = f.filter;
  const who = label(f).replaceAll("`", "");
  return (
    <div className="flex flex-wrap items-center gap-2">
      {filter && (
        <>
          <Button type="button" size="sm" onClick={() => params.addFilter(filter)}>
            <FilterIcon aria-hidden size={14} /> Filter to these visitors<span className="sr-only">: {who}</span>
          </Button>
          <ButtonLink size="sm" to={link(appId, "journeys", filter)}>
            <Route aria-hidden size={14} /> See their journeys<span className="sr-only">: {who}</span>
          </ButtonLink>
        </>
      )}
      <AskWhy appId={appId} finding={{ kind: "trait", trait: { kind: f.kind, field: f.field, key: f.key, label: f.label }, goal: report.target.label.slice(0, 120), lift: f.lift }} />
    </div>
  );
}

/** The trait as a short label: "Used Android", "Saw /pricing". */
function label(f: LiftFeature): string {
  const p = liftPhrase(f);
  return p.charAt(0).toUpperCase() + p.slice(1);
}

/** Which kind of trait, so a mixed list can be scanned. */
function kindLabel(f: LiftFeature): string {
  switch (f.kind) {
    case "event":
      return "Did";
    case "outbound":
      return "Left for";
    case "friction":
      return "Friction";
    case "vital":
      return "Speed";
    case "survey":
      return "Answer";
  }
  switch (f.field) {
    case "path":
    case "entry_path":
      return "Page";
    case "referrer_domain":
    case "utm_source":
    case "utm_medium":
    case "utm_campaign":
      return "Source";
    case "country":
      return "Where";
    case "device_type":
    case "os":
    case "browser":
    case "platform":
      return "Device";
    case "a11y":
      return "Setting";
    default:
      return "Trait";
  }
}

/** How much more often, as compact as it can be said. */
function moreWord(f: LiftFeature): string {
  if (f.lift == null || !Number.isFinite(f.lift)) return fmtPoints(f.points / 100);
  if (f.lift >= 1.9) return `${Math.round(f.lift * 2) / 2}×`;
  return `+${Math.round((f.lift - 1) * 100)}%`;
}

/** One scale for every bar in a list, rounded up so low rates aren't squashed against zero. */
function scaleMax(features: LiftFeature[]): number {
  const top = Math.max(0.01, ...features.flatMap((f) => [f.rate, f.rateWithout])) * 1.1;
  return [0.02, 0.05, 0.1, 0.2, 0.25, 0.5, 0.75, 1].find((s) => s >= top) ?? 1;
}

/** Backticked event names as code; "×" read as "times". */
function Words({ text }: { text: string }) {
  return (
    <>
      {text.split("`").map((part, i) =>
        i % 2 ? (
          <InlineCode key={i}>{part}</InlineCode>
        ) : (
          <Fragment key={i}>{times(part)}</Fragment>
        ),
      )}
    </>
  );
}

function times(s: string): ReactNode {
  const parts = s.split("×");
  return parts.map((p, i) => (
    <Fragment key={i}>
      {p}
      {i < parts.length - 1 && (
        <>
          <span aria-hidden>×</span>
          <span className="sr-only"> times</span>
        </>
      )}
    </Fragment>
  ));
}
